import "native-injects";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {
    BudgetComponentType,
    FinancialEntityType,
    GetChartOfAccountsQuery,
    OperationalFinancialMetricsAggregateType,
    useGetChartOfAccountsLazyQuery
} from "../../__generated__/generated_types";
import {buildChartOfAccountsData, FinancialEntity} from "./logic";
import {AuthContext} from "../AuthContext";
import {getBudgetYearForProperty, useProperties} from "../properties/PropertiesContext";
import {GuestAuthenticationContext} from "../guest-auth-context/GuestAuthContext";

export type { FinancialEntity, ComponentOp } from "./logic";
export interface Account {
    id: string;
    accountNumber: string;
    name: string;
    order: number;
    parentId: string;
}

interface ChartOfAccountsInternalState {
    accounts: Account[];
    chartOfAccountsId: string | undefined;
    chartOfAccounts: FinancialEntity[] | undefined;
    chartOfAccountsFlat: FinancialEntity[] | undefined;
    financialEntityInfoByIdMap: Record<string, FinancialEntity>;
    parents: Record<string, FinancialEntity>;
    isIncome: Record<string, boolean>;
    rawData: GetChartOfAccountsQuery | undefined;
    loading: boolean;
    operationalFinancialMetricsAggregateId: string | undefined;
    operationalFinancialMetricsAggregateType: OperationalFinancialMetricsAggregateType | undefined;
    isReady: boolean;
}

export interface CategoryDescriptor {
    id: string,
    type: FinancialEntityType, //'Component' | 'Category' | 'Account',
    name: string,
    budgetComponentType?: 'INCOME' | 'EXPENSE',
}

export interface ChartOfAccountsState extends ChartOfAccountsInternalState {
    getPrevAccountId: (accountId: string) => string | undefined;
    getNextAccountId: (accountId: string) => string | undefined;
    getParentCategories: (firstParentId: string) => CategoryDescriptor[];
    getIsIncome: (id: string) => boolean;
    getFinancialEntityById: (id: string) => FinancialEntity | undefined;
}

const DEFAULT_INTERNAL_STATE: ChartOfAccountsInternalState = {
    accounts: [],
    rawData: undefined,
    chartOfAccountsId: undefined,
    chartOfAccounts: undefined,
    chartOfAccountsFlat: undefined,
    financialEntityInfoByIdMap: {},
    parents: {},
    isIncome: {},
    loading: false,
    operationalFinancialMetricsAggregateId: undefined,
    operationalFinancialMetricsAggregateType: undefined,
    isReady: false
};

const ChartOfAccountsStateContext = React.createContext<ChartOfAccountsState | undefined>(undefined);

function useChartOfAccountsState({isGuest}: {isGuest: boolean}): ChartOfAccountsState {
    const [budgetYear, setBudgetYear] = useState<number | undefined>(undefined);
    const {currentProperty} = useProperties();

    const auth = useContext(AuthContext);
    const guestContext = useContext(GuestAuthenticationContext);

    const {budgetYear: authBudgetYear, clientId} = useMemo(() => {
        if(isGuest) {
            return {
                budgetYear: guestContext.user?.property.budgetYear ?? 0,
                clientId: guestContext.user?.clientId ?? ""
            };
        } else {
            return {
                budgetYear: auth.budgetYear,
                clientId: auth.user?.clientId ?? ""
            };
        }
    }, [isGuest, auth, guestContext]);

    const [loadChartOfAccounts, {data: data, loading: loading}] = useGetChartOfAccountsLazyQuery({
        fetchPolicy: "no-cache"
    });

    useEffect(() => {
            setBudgetYear(getBudgetYearForProperty(currentProperty, authBudgetYear));
    }, [currentProperty, authBudgetYear]);

    const [state, setState] = useState(DEFAULT_INTERNAL_STATE);

    useEffect(() => {
        if (!budgetYear) {
            return;
        }
        loadChartOfAccounts({
            variables: {
                budgetYear: budgetYear
            }
        });
    }, [clientId, budgetYear]);

    function getPrevAccountId(accountId: string) {
        if (state.accounts === undefined || state.accounts.length === 0) {
            return undefined;
        }
        const accountIndex = state.accounts.findIndex(acc => acc.id === accountId);
        if (accountIndex === 0) {
            return undefined;
        }

        return state.accounts[accountIndex - 1]?.id ?? '';
    }

    function getNextAccountId(accountId: string) {
        if (state.accounts === undefined || state.accounts.length === 0) {
            return undefined;
        }
        const accountIndex = state.accounts.findIndex(acc => acc.id === accountId);
        if (accountIndex === state.accounts.length - 1) {
            return undefined;
        }

        return state.accounts[accountIndex + 1]?.id ?? '';
    }

    function getParentCategories(firstParentId: string): CategoryDescriptor[] {
        const parentCategories = [] as CategoryDescriptor[];

        if (!state.isReady || !state.accounts) {
            return [];
        }

        let fe = state.parents[firstParentId];

        while (fe?.parentId) {
            parentCategories.push({
                id: fe.id,
                name: fe.name,
                type: fe.type,
                budgetComponentType: fe.budgetComponentType === BudgetComponentType.Income ? "INCOME" : "EXPENSE"
            });
            fe = state.parents[fe.parentId];
        }

        if (fe) {
            parentCategories.push({
                id: fe.id,
                name: fe.name,
                type: fe.type,
                budgetComponentType: fe.budgetComponentType === BudgetComponentType.Income ? "INCOME" : "EXPENSE"
            });
        }

        return parentCategories;
    }

    function getIsIncome(entityId: string): boolean {
        return state.isIncome[entityId] ?? false;
    }

    function getFinancialEntityById(entityId: string): FinancialEntity | undefined {
        return state.financialEntityInfoByIdMap[entityId];
    }

    useEffect(
        () => {
            if (data && !loading && data.listChartOfAccounts) {
                const {chartOfAccounts, chartOfAccountsFlat, parents, isIncome}
                    = buildChartOfAccountsData(data.listChartOfAccounts.records, data.listChartOfAccounts.budgetComponentMappings);
                const accountsCollection: Account[] = chartOfAccountsFlat
                    .filter(row => row.type == FinancialEntityType.Account)
                    .map(row => ({
                        id: row.id,
                        accountNumber: row.number,
                        name: row.name,
                        order: row.order,
                        parentId: row.parentId ?? ""
                    }));

                const financialEntityInfoByIdMap = chartOfAccountsFlat.toIdMap("id");

                setState({
                    ...state,
                    financialEntityInfoByIdMap: financialEntityInfoByIdMap,
                    accounts: accountsCollection.sortBy(account => account.order),
                    rawData: data,
                    chartOfAccountsId: data.listChartOfAccounts.id,
                    chartOfAccounts: chartOfAccounts,
                    chartOfAccountsFlat: chartOfAccountsFlat,
                    parents,
                    isIncome: isIncome,
                    loading: loading,
                    operationalFinancialMetricsAggregateId: data.listChartOfAccounts.operationalFinancialMetricsAggregateId,
                    operationalFinancialMetricsAggregateType: data.listChartOfAccounts.operationalFinancialMetricsAggregateType,
                    isReady: true
                });
            }
        },
        [data, loading]
    );

    return {
        ...state,
        getIsIncome,
        getNextAccountId,
        getPrevAccountId,
        getParentCategories,
        getFinancialEntityById,
    };
}

export const ChartOfAccountsProvider: React.FunctionComponent<{isGuest?: boolean}> = ({children, isGuest}) => {
    const state = useChartOfAccountsState({isGuest: isGuest === true});

    if (!state.chartOfAccounts) {
        // We want to prevent rendering anything until the chart of accounts is loaded
        return <></>;
    }
    return (
        <ChartOfAccountsStateContext.Provider value={state}>
            {children}
        </ChartOfAccountsStateContext.Provider>
    );
};

export function useChartOfAccounts(): ChartOfAccountsState {
    const context = React.useContext(ChartOfAccountsStateContext);

    if (context === undefined) {
        throw new Error('useChartOfAccounts must be used within a ChartOfAccountsProvider');
    }

    return context;
}


function addChildren(parent: FinancialEntity, result: FinancialEntity[], categoryBottom = false) {
    for (const child of parent.children.sortBy("order")) {
        if (!categoryBottom) {
            result.push(child);
        }
        addChildren(child, result, categoryBottom);
        if (categoryBottom) {
            result.push(child);
        }
    }
}

export function buildChartOfAccountsFlatComponentsBottomLine(chartOfAccountsTree: FinancialEntity[], categoryBottom = false): FinancialEntity[] {
    const chartOfAccountsFlat: FinancialEntity[] = [];
    for (const record of chartOfAccountsTree.sortBy("order")) {
        addChildren(record, chartOfAccountsFlat, categoryBottom);
        chartOfAccountsFlat.push(record);
    }
    return chartOfAccountsFlat;
}

export function orderByChartOfAccounts<T>(coaSorted: FinancialEntity[], toOrder: Map<string, T>): T[] {
    const resultRows = [];
    for (const coaRow of coaSorted) {
        const resultRow = toOrder.get(coaRow.id);
        if (resultRow) {
            resultRows.push(resultRow);
        }
    }

    return resultRows;
}

